export interface Prefill {
  name?: string;
  email?: string;
  contact?: string;
  /** ISO country and postcode from the workspace billing address; Paddle skips its details step with them. */
  countryCode?: string;
  postalCode?: string;
}
export interface RazorpayOrder {
  order_id: string;
  amount: number;
  currency: string;
  key_id?: string;
}
export type PaymentOutcome = 'paid' | 'activated' | 'dismissed';
export class CheckoutError extends Error {
  constructor(
    message: string,
    public orderId: string,
    public paymentReceived = false,
  ) {
    super(message);
    this.name = 'CheckoutError';
  }
}
type RazorpayInstance = { open(): void; on(event: string, cb: (payload: unknown) => void): void };
declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => RazorpayInstance;
  }
}

/** INR checkout: preserve international numbers; qualify Indian local numbers. */
export function checkoutPrefill(prefill: Prefill): Prefill {
  const raw = prefill.contact?.trim() || '';
  const digits = raw.replace(/\D/g, '');
  const contact =
    raw.startsWith('+') && /^[1-9]\d{6,14}$/.test(digits)
      ? `+${digits}`
      : /^[6-9]\d{9}$/.test(digits)
        ? `+91${digits}`
        : /^91[6-9]\d{9}$/.test(digits)
          ? `+${digits}`
          : undefined;
  return { name: prefill.name, email: prefill.email, ...(contact ? { contact } : {}) };
}

export async function openCheckout(
  order: RazorpayOrder,
  description: string,
  prefill: Prefill,
  options: {
    keyId: string;
    verify: (response: Record<string, string>) => Promise<unknown>;
    onOpen?: () => Promise<void>;
    onFailure?: () => void;
  },
): Promise<PaymentOutcome> {
  const Razorpay = window.Razorpay;
  if (!Razorpay) throw new Error('Payment window unavailable.');
  await options.onOpen?.();
  return new Promise((resolve, reject) => {
    let settled = false;
    let failure: string | null = null;
    const rzp = new Razorpay({
      key: order.key_id || options.keyId,
      amount: order.amount,
      currency: order.currency,
      order_id: order.order_id,
      name: 'CallSync',
      description,
      prefill: checkoutPrefill(prefill),
      theme: { color: getComputedStyle(document.documentElement).getPropertyValue('--primary').trim() },
      handler: async (response: Record<string, string>) => {
        if (settled) return;
        settled = true;
        try {
          await options.verify(response);
          resolve('paid');
        } catch {
          reject(
            new CheckoutError(
              'Payment received, but confirmation is pending. Please contact support before paying again.',
              order.order_id,
              true,
            ),
          );
        }
      },
      modal: {
        confirm_close: true,
        ondismiss: () => {
          if (settled) return;
          settled = true;
          if (failure) reject(new CheckoutError(failure, order.order_id));
          else resolve('dismissed');
        },
      },
    });
    // Let customers retry inside Razorpay. A later success wins over earlier failures.
    rzp.on('payment.failed', (payload) => {
      if (settled) return;
      options.onFailure?.();
      const error = (payload as { error?: { description?: string } } | null)?.error;
      failure = error?.description || 'Payment failed. You can try again.';
    });
    rzp.open();
  });
}
